"use client";

import { useEffect, useRef, FormEvent, useState } from "react";
import { X, Send, CheckCircle } from "lucide-react";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formOpenTime, setFormOpenTime] = useState<number>(0);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Focus first input when modal opens and reset states
  useEffect(() => {
    if (isOpen) {
      setIsSuccess(false);
      setErrorMessage(null);
      setFormOpenTime(Date.now()); // Track when form was opened
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);

    // Anti-spam: Check honeypot field (should be empty)
    const honeypot = formData.get("website") as string;
    if (honeypot) {
      // Bot detected - fail silently
      console.warn("Spam detected: honeypot filled");
      setErrorMessage("Something went wrong. Please try again.");
      setIsSubmitting(false);
      return;
    }

    // Anti-spam: Check time-based validation
    const timeSpent = Date.now() - formOpenTime;
    if (timeSpent < 3000) {
      // Too fast - likely a bot
      console.warn("Spam detected: form submitted too quickly");
      setErrorMessage("Please take your time filling out the form.");
      setIsSubmitting(false);
      return;
    }
    if (timeSpent > 1800000) {
      // 30 minutes - form expired
      console.warn("Form expired: open for too long");
      setErrorMessage("Form session expired. Please refresh and try again.");
      setIsSubmitting(false);
      return;
    }

    const data = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      message: formData.get("message") as string,
      formOpenTime: formOpenTime, // Send to backend for validation
    };

    try {
      setErrorMessage(null);

      // Send demo request to backend
      const response = await fetch("/api/contact/demo-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        // Try to get error details from response
        let errorMsg = "Failed to send demo request";
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            // Handle validation errors
            if (Array.isArray(errorData.detail)) {
              errorMsg = errorData.detail.map((err: any) => err.msg).join(", ");
            } else {
              errorMsg = errorData.detail;
            }
          }
        } catch {
          // If can't parse error, use generic message
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();

      // Show success state
      setIsSuccess(true);
    } catch (error) {
      console.error("Error submitting demo request:", error);
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Trap focus inside modal
  const handleTabKey = (e: React.KeyboardEvent) => {
    const focusableElements = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    if (e.key === "Tab") {
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-s4 animate-fade-in overflow-y-auto"
      onClick={handleBackdropClick}
      onKeyDown={handleTabKey}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-md bg-gradient-to-br from-slate-900 via-purple-900/90 to-slate-900 border border-brand/30 rounded-xl shadow-lg p-s6 md:p-s8 animate-scale-in my-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 end-4 text-brand hover:text-white transition-colors p-s1"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Success State */}
        {isSuccess ? (
          <div className="text-center py-s8">
            <div className="flex justify-center mb-s4">
              <CheckCircle className="h-16 w-16 text-success" />
            </div>
            <h2 className="text-lg md:text-3xl font-bold mb-s4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Thank You!
            </h2>
            <p className="text-brand text-sm mb-s4 leading-relaxed">
              We&apos;ve received your demo request and we&apos;re excited to show you what Storita AI can do!
            </p>
            <p className="text-brand text-sm mb-s6">
              Our team will review your request and get back to you soon.
            </p>
            <button
              onClick={onClose}
              className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-[length:200%_100%] animate-gradient-x text-white px-s8 py-s3 rounded-xl font-medium transition-transform hover:scale-105 shadow-lg"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Title */}
            <h2
              id="modal-title"
              className="text-lg md:text-3xl font-bold mb-s2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
            >
              Request a Demo
            </h2>
            <p className="text-brand text-sm mb-s6">
              Fill out the form below and we&apos;ll get back to you soon.
            </p>

            {/* Error Message */}
            {errorMessage && (
              <div className="mb-s4 p-s3 bg-error/10 border border-error/30 rounded-lg">
                <p className="text-error text-sm">
                  {errorMessage}
                </p>
                <p className="text-error/70 text-xs mt-s1">
                  Please try again or email us directly at arik@digitalforce.co.il
                </p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-s4">
          {/* Name field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-brand mb-s1">
              Name *
            </label>
            <input
              ref={nameInputRef}
              type="text"
              id="name"
              name="name"
              required
              minLength={1}
              maxLength={100}
              className="w-full px-s4 py-s2 bg-white/5 border border-brand/30 rounded-lg text-white placeholder-purple-300/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] transition-colors"
              placeholder="Your name"
            />
          </div>

          {/* Email field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-brand mb-s1">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full px-s4 py-s2 bg-white/5 border border-brand/30 rounded-lg text-white placeholder-purple-300/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] transition-colors"
              placeholder="your@email.com"
            />
          </div>

          {/* Message field */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-brand mb-s1">
              Message * <span className="text-brand text-xs">(min. 10 characters)</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              minLength={10}
              maxLength={1000}
              rows={4}
              className="w-full px-s4 py-s2 bg-white/5 border border-brand/30 rounded-lg text-white placeholder-purple-300/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-primary))] transition-colors resize-none"
              placeholder="Tell us about your needs... (at least 10 characters)"
            />
          </div>

          {/* Honeypot field - hidden from humans, visible to bots */}
          <div className="sr-only" aria-hidden="true">
            <label htmlFor="website">Website (leave blank)</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-s3 pt-s2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 relative overflow-hidden group bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-[length:200%_100%] animate-gradient-x text-white px-s6 py-s3 rounded-xl font-medium transition-transform hover:scale-105 shadow-lg flex items-center justify-center gap-s2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <span>{isSubmitting ? "Sending..." : "Send Request"}</span>
              <Send className="h-4 w-4" />
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-60 group-hover:opacity-80 animate-shimmer" />
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white px-s6 py-s3 rounded-xl font-medium transition-colors hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Cancel
            </button>
          </div>
        </form>
          </>
        )}
      </div>
    </div>
  );
}
